import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { type KeyboardEvent, useContext, useEffect, useState } from 'react';
import { EmailAuthProvider, linkWithCredential } from 'firebase/auth';
import type { FirebaseError } from 'firebase/app';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';
import { errorToastOptions, getAuthErrorMessage, successToastOptions, validatePassword } from '@/helpers';
import { useToast } from '@chakra-ui/react';
import { FirebaseContext } from '@/session/FirebaseContext';

export function SetPasswordModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onNeedsReauth: () => void;
}) {
  const ctx = useContext(FirebaseContext);
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const { isMobile, modalSize, isCentered, scrollBehavior, contentProps, footerPb, titleFontSize } =
    useMobileFormModal();

  useEffect(() => {
    if (props.isOpen) {
      setPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setSubmitAttempted(false);
      setIsBusy(false);
    }
  }, [props.isOpen]);

  async function linkPassword() {
    const user = ctx.user;
    if (!user?.email) return;

    setSubmitAttempted(true);
    setPasswordError('');

    const pwCheck = validatePassword(password, confirmPassword);
    if (!pwCheck.valid) {
      setPasswordError(pwCheck.error ?? 'Invalid password.');
      return;
    }

    setIsBusy(true);
    try {
      await linkWithCredential(user, EmailAuthProvider.credential(user.email, password));
      toast(successToastOptions('Password set', 'You can now sign in with email and password.'));
      props.onClose();
    } catch (err) {
      const firebaseErr = err as FirebaseError;
      if (firebaseErr.code === 'auth/requires-recent-login') {
        props.onClose();
        props.onNeedsReauth();
      } else {
        toast(errorToastOptions('Failed to set password', getAuthErrorMessage(firebaseErr)));
      }
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isCentered={isCentered}
      size={modalSize}
      scrollBehavior={scrollBehavior}
    >
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <ModalHeader>
          <Heading fontSize={titleFontSize}>Set password</Heading>
        </ModalHeader>
        <ModalCloseButton mt={2} mr={2} />
        <ModalBody px={{ base: 4, md: 6 }}>
          <Stack spacing={4}>
            <FormControl isInvalid={submitAttempted && passwordError.length > 0} isRequired>
              <FormLabel>New password</FormLabel>
              <InputGroup>
                <Input
                  size={isMobile ? 'lg' : 'md'}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete='new-password'
                />
                <InputRightElement width='4.5rem'>
                  <Button h='1.75rem' size='sm' onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              {passwordError ? <FormErrorMessage>{passwordError}</FormErrorMessage> : null}
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Confirm password</FormLabel>
              <InputGroup>
                <Input
                  size={isMobile ? 'lg' : 'md'}
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete='new-password'
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') void linkPassword();
                  }}
                />
                <InputRightElement width='4.5rem'>
                  <Button h='1.75rem' size='sm' onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter pb={footerPb}>
          <Button
            w={{ base: 'full', md: 'auto' }}
            isLoading={isBusy}
            colorScheme='green'
            onClick={() => void linkPassword()}
          >
            Set password
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
